import { EmploymentProfile } from '../types/employment.model'
import {
  EmploymentProfileDto,
  HOURLY_RATE_MINOR_UNITS_MIN,
  UpsertEmploymentProfileDto,
  WEEKLY_HOURS_LIMIT_MIN
} from './EmploymentProfilesAPI.model'

/** The list keyed by employee, the shape every consumer reads it in. */
export const toProfilesByUserId = (
  profiles: EmploymentProfileDto[]
): Record<string, EmploymentProfile> =>
  profiles.reduce<Record<string, EmploymentProfile>>((acc, profile) => {
    acc[profile.userId] = profile
    return acc
  }, {})

/** The value if the backend would accept it, otherwise nothing at all. */
const atLeast = (value: number | undefined, min: number): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value >= min
    ? value
    : undefined

/**
 * The body for a PUT.
 *
 * Both numeric fields have a backend minimum of 1, so a blank or zeroed input
 * is dropped rather than sent: a literal 0 comes back as a 400 that fails the
 * whole save. Dropping it loses nothing. PUT is a full replace, and an absent
 * field is exactly how the backend records "not set".
 */
export const toUpsertDto = (
  profile: EmploymentProfile
): UpsertEmploymentProfileDto => {
  const hourlyRateMinorUnits = atLeast(
    profile.hourlyRateMinorUnits,
    HOURLY_RATE_MINOR_UNITS_MIN
  )
  const weeklyHoursLimit = atLeast(
    profile.weeklyHoursLimit,
    WEEKLY_HOURS_LIMIT_MIN
  )

  return {
    ...(profile.position ? { position: profile.position } : {}),
    ...(profile.contractType ? { contractType: profile.contractType } : {}),
    ...(hourlyRateMinorUnits !== undefined ? { hourlyRateMinorUnits } : {}),
    ...(weeklyHoursLimit !== undefined ? { weeklyHoursLimit } : {})
  }
}

/**
 * True when nothing worth storing is left. Such a profile is never written: on
 * create it would only add an all-null row, and on edit it means the user
 * cleared every employment field, which is a deletion.
 */
export const isEmptyProfile = (profile: EmploymentProfile): boolean =>
  Object.keys(toUpsertDto(profile)).length === 0
